import React, { useCallback, useEffect, useRef, useState } from 'react';

import { apiFetch } from '@/services/api';

interface Prediction {
  placeId: string;
  description: string;
}

interface AddressData {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryCode: string;
  formattedAddress?: string;
}

interface GoogleAddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (address: {
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    countryCode: string;
  }) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
}

export function GoogleAddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  className,
  id,
  disabled,
  required,
}: Readonly<GoogleAddressAutocompleteProps>) {
  const [suggestions, setSuggestions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const skipNextSearch = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (input.trim().length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    try {
      const res = await apiFetch<{ success: boolean; data: { predictions: Prediction[] } }>(
        `/v1/places/autocomplete?input=${encodeURIComponent(input.trim())}&countryCode=us`
      );
      const preds = res?.data?.predictions ?? [];
      setSuggestions(preds);
      setShowDropdown(preds.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    onChange(text);
    setLocationError(null);

    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(text), 250);
  }, [onChange, fetchSuggestions]);

  const handleSelectSuggestion = useCallback(async (prediction: Prediction) => {
    skipNextSearch.current = true;
    onChange(prediction.description);
    setSuggestions([]);
    setShowDropdown(false);

    try {
      const res = await apiFetch<{ success: boolean; data: AddressData }>(
        `/v1/places/details?placeId=${encodeURIComponent(prediction.placeId)}`
      );
      const addr = res?.data;
      if (addr) {
        if (addr.streetAddress) {
          skipNextSearch.current = true;
          onChange(addr.streetAddress);
        }
        onPlaceSelected(addr);
      }
    } catch {
      // Keep the description in the field; parent can still submit
    }
  }, [onChange, onPlaceSelected]);

  const handleClear = useCallback(() => {
    // Cancel any pending debounced search so no Places API call fires.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    onChange('');
    setSuggestions([]);
    setShowDropdown(false);
    setIsSearching(false);
    setLocationError(null);
    inputRef.current?.focus();
  }, [onChange]);

  const handleUseCurrentLocation = useCallback(async () => {
    if (isLocating) return;
    setIsLocating(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError('Location is not supported by your browser.');
      setIsLocating(false);
      return;
    }

    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 60000,
        });
      });
      const { latitude: lat, longitude: lng } = pos.coords;
      const res = await apiFetch<{ success: boolean; data: AddressData }>(
        `/v1/places/reverse-geocode?lat=${lat}&lng=${lng}`
      );
      const addr = res?.data;
      if (addr && (addr.streetAddress || addr.formattedAddress)) {
        const street = addr.streetAddress || addr.formattedAddress || '';
        skipNextSearch.current = true;
        onChange(street);
        onPlaceSelected(addr);
      } else {
        setLocationError("Couldn't determine your address. Please type it.");
      }
    } catch (err) {
      if (err instanceof GeolocationPositionError) {
        if (err.code === GeolocationPositionError.PERMISSION_DENIED) {
          setLocationError('Location permission denied. Allow access in browser settings.');
        } else if (err.code === GeolocationPositionError.TIMEOUT) {
          setLocationError('Location timed out. Please try again or type your address.');
        } else {
          setLocationError("Couldn't get your location. Please type your address.");
        }
      } else {
        setLocationError("Couldn't get your location. Please type your address.");
      }
    } finally {
      setIsLocating(false);
    }
  }, [isLocating, onChange, onPlaceSelected]);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={className}
          // Reserve space on the right whenever the spinner or clear (X) icon
          // is shown so long text can't render underneath and hide it. Inline
          // style beats the parent's px-3 class regardless of CSS order.
          style={isSearching || (value && !disabled) ? { paddingRight: '2.25rem' } : undefined}
          autoComplete="off"
          disabled={disabled}
          required={required}
        />
        {isSearching ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="h-4 w-4 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </span>
        ) : (
          value && !disabled && (
            <button
              type="button"
              aria-label="Clear address"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
          {suggestions.map((s) => (
            <li key={s.placeId}>
              <button
                type="button"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-start gap-2"
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent input blur before click registers
                  handleSelectSuggestion(s);
                }}
              >
                <svg className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                  <circle cx="12" cy="9" r="2.5" fill="currentColor" />
                </svg>
                <span>{s.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={handleUseCurrentLocation}
        disabled={isLocating || disabled}
        className="flex items-center gap-1.5 text-xs text-primary mt-1 hover:underline disabled:opacity-50"
      >
        {isLocating ? (
          <span>Detecting location…</span>
        ) : (
          <>
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              <circle cx="12" cy="9" r="2.5" fill="currentColor" />
            </svg>
            <span>Use my current location</span>
          </>
        )}
      </button>

      {locationError && (
        <p className="text-xs text-red-500 mt-1">{locationError}</p>
      )}
    </div>
  );
}
